import { useTranslations } from "@/i18n/useTranslations";
import { useHomepageConfig } from "@/hooks/use-homepage-config";
import { FileText, Phone, Mail, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";

const COLLAPSED_KEY = "enrollment-consult-collapsed";

export function EnrollmentConsultSidebar() {
  const t = useTranslations("settings");
  const { settings } = useHomepageConfig();

  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  });

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  const consult = settings.enrollmentConsult;
  if (!consult?.enabled) return null;

  const hasInstructions = (consult.enrollmentInstructions?.length ?? 0) > 0;
  const hasContacts = consult.contacts.length > 0;
  if (!hasInstructions && !hasContacts) return null;

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col items-center gap-1 bg-gradient-to-b from-primary to-primary-dark text-primary-foreground rounded-l-xl px-2 py-3 shadow-glass-sm hover:shadow-glass hover:from-primary hover:to-primary-dark transition-all duration-200"
        aria-label={t("homepage.enrollmentConsult.title")}
      >
        <FileText className="w-4 h-4" />
        <span className="text-[10px] font-medium" style={{ writingMode: "vertical-rl", letterSpacing: "2px" }}>
          {t("homepage.enrollmentConsult.title")}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 p-1.5">
      {hasInstructions && (
        <a
          href="/enrollment-instruction"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-2 bg-overlay/80 backdrop-blur-xl rounded-l-xl px-3 py-2.5 shadow-glass-sm hover:shadow-glass hover:bg-white transition-all duration-200 text-foreground dark:text-muted-foreground"
        >
          <FileText className="w-4 h-4 text-primary shrink-0" />
          <span className="text-xs font-medium whitespace-nowrap">
            {t("homepage.enrollmentConsult.instruction")}
          </span>
        </a>
      )}

      {hasContacts && consult.contacts.map((contact, idx) => (
        <div
          key={idx}
          className="bg-overlay/80 backdrop-blur-xl rounded-l-xl px-3 py-2 shadow-glass-sm text-muted-foreground dark:text-muted-foreground"
        >
          <p className="text-[11px] font-medium text-foreground dark:text-slate-100 truncate">{contact.name}</p>
          <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-[10px] hover:text-primary transition-colors">
            <Phone className="w-2.5 h-2.5 shrink-0" />
            <span className="truncate">{contact.phone}</span>
          </a>
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-[10px] hover:text-primary transition-colors">
              <Mail className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">{contact.email}</span>
            </a>
          )}
        </div>
      ))}

      <button
        onClick={() => setCollapsed(true)}
        className="self-center text-[10px] text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground transition-colors flex items-center gap-0.5"
      >
        <ChevronRight className="w-3 h-3" />
        {t("homepage.enrollmentConsult.collapse")}
      </button>
    </div>
  );
}
